import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Grid, GridSize, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  Document_UploadFileMutation,
  DocumentTagLinkedField,
  Estate_FieldsFragment,
  Estate_GetEstateByIdDocument,
  EstateStatus,
  EstateType,
  EstateWorkItem_GetEstateWorkItemsCountsByPortalIdDocument,
  useDocumentTag_GetAllDocumentTagsQuery,
  useEstate_UpdateEstateMutation,
} from 'queries';

import Spinner from 'common/spinner';
import DocumentUploadButton from 'pages/common/document/document-upload-button';
import { allDocumentsUploaded } from 'utils/estate';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

import EstateDocumentPaper from './estate-document-paper';
type Props = {
  documentId?: string;
  estateDocumentField: string;
  estate: Estate_FieldsFragment;
};

const useStyles = makeStyles(() => ({
  documentAction: {
    textAlign: 'center',
  },
  documentDescription: {
    padding: '5px',
    paddingBottom: '15px',
  },
  documentRow: {
    paddingTop: '15px',
    paddingBottom: '15px',
  },
  documentTruadminIcon: {
    textAlign: 'center',
    height: '48px',
  },
  documentTruplanningIcon: {
    textAlign: 'center',
    height: '108px',
  },
  documentTruproxyIcon: {
    textAlign: 'center',
    height: '108px',
  },
  instruction: {
    display: 'block',
    fontSize: '.65rem',
    lineHeight: '1.3',
  },
}));

type EstateDocumentDetail = {
  [key: string]: {
    img: string;
    imgAlt: string;
    description: string;
    instruction: string;
    documentTagId?: string;
  };
};

export default function EstateDocumentUploadBox({
  documentId,
  estateDocumentField,
  estate,
}: Props) {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();
  const [currentDocumentId, setCurrentDocumentId] = useState(documentId);

  const gridColSize: GridSize =
    estate.type === EstateType.Truadmin || estate.type === EstateType.Trustadmin
      ? 6
      : estate.type === EstateType.Truplanning
        ? 6
        : estate.type === EstateType.Truproxy
          ? 4
          : 'auto';

  const documentIconClass: string =
    estate.type === EstateType.Truadmin || estate.type === EstateType.Trustadmin
      ? classes.documentTruadminIcon
      : estate.type === EstateType.Truplanning
        ? classes.documentTruplanningIcon
        : estate.type === EstateType.Truproxy
          ? classes.documentTruproxyIcon
          : '';

  const { data: documentTagsData, loading: documentTagsDataLoading } =
    useDocumentTag_GetAllDocumentTagsQuery({
      onError: (error) => {
        showAlert({
          message: 'Error getting all document tags: ' + error.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const documentDetail = useMemo(() => {
    const documentTags = documentTagsData?.data || [];
    const documentIdLettersTestamentaryDocumentTagId = documentTags.find(
      (tag) => tag.linkedField === DocumentTagLinkedField.DocumentIdLettersTestamentary,
    )?.id;
    const documentIdDeathCertificateDocumentTagId = documentTags.find(
      (tag) => tag.linkedField === DocumentTagLinkedField.DocumentIdDeathCertificate,
    )?.id;
    const documentIdBackOfPhotoIdDocumentTagId = documentTags.find(
      (tag) => tag.linkedField === DocumentTagLinkedField.DocumentIdBackOfPhotoId,
    )?.id;
    const documentIdFrontOfPhotoIdDocumentTagId = documentTags.find(
      (tag) => tag.linkedField === DocumentTagLinkedField.DocumentIdFrontOfPhotoId,
    )?.id;
    const documentIdLegalDocumentationDocumentTagId = documentTags.find(
      (tag) => tag.linkedField === DocumentTagLinkedField.DocumentIdLegalDocumentation,
    )?.id;

    const data: EstateDocumentDetail = {
      documentIdLettersTestamentary: {
        // truadmin
        img: '/letter.svg',
        imgAlt: 'legal documentation',
        description: 'Legal Documentation',
        instruction: `This is the document appointing your client
        as the legal representative of the deceased's estate.
        Acceptable documents include: Letters Testamentary,
        Letters of Administration, Executor's Certificate, Small Estate Affidavit,
        Certificate of Voluntary Administration, and,
        if a surviving spouse, a marriage certificate.`,
        documentTagId: documentIdLettersTestamentaryDocumentTagId,
      },
      documentIdDeathCertificate: {
        // truadmin
        img: '/certificate.svg',
        imgAlt: 'death certificate',
        description: 'Death Certificate',
        instruction: '',
        documentTagId: documentIdDeathCertificateDocumentTagId,
      },
      documentIdFrontOfPhotoId: {
        // truplanning and truproxy
        img: '/front-of-id.svg',
        imgAlt: 'front of photo id',
        description: 'Front of ID',
        instruction: '',
        documentTagId: documentIdFrontOfPhotoIdDocumentTagId,
      },
      documentIdBackOfPhotoId: {
        // truplanning and truproxy
        img: '/back-of-id.svg',
        imgAlt: 'back of photo id',
        description: 'Back of ID',
        instruction: '',
        documentTagId: documentIdBackOfPhotoIdDocumentTagId,
      },
      documentIdLegalDocumentation: {
        // truproxy
        img: '/legal-documentation.svg',
        imgAlt: 'legal documentation',
        description: 'Legal Documentation',
        instruction: 'i.e. the guardianship decree or validly executed power of attorney',
        documentTagId: documentIdLegalDocumentationDocumentTagId,
      },
    };
    return data;
  }, [documentTagsData?.data]);

  const [update] = useEstate_UpdateEstateMutation({
    refetchQueries: [
      {
        query: EstateWorkItem_GetEstateWorkItemsCountsByPortalIdDocument,
        variables: { portalId },
      },
      {
        query: Estate_GetEstateByIdDocument,
        variables: { id: estate.id },
      },
    ],
    onError: (e) => {
      showAlert({
        message: 'Error updating estate: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  if (!documentTagsData || documentTagsDataLoading) {
    return <Spinner />;
  }

  // after the upload is complete, insert the S3 id and name into the state and save
  const uploadCompleted = (uploadFileData: Document_UploadFileMutation) => {
    const moveForward = allDocumentsUploaded(estate, estateDocumentField);
    const status = moveForward ? EstateStatus.Payment : EstateStatus.DocumentUpload;
    setCurrentDocumentId(uploadFileData.data.id);
    // update the database
    update({
      variables: {
        input: {
          id: estate.id || '',
          portalId: portalId,
          [estateDocumentField]: uploadFileData.data.id,
          status: status,
        },
      },
    });
  };

  return (
    <Grid
      key={estateDocumentField}
      className={classes.documentRow}
      item
      xs={gridColSize}
      sm={gridColSize}
    >
      <Grid className={documentIconClass} item xs={12} sm={12}>
        <img
          src={documentDetail[estateDocumentField].img}
          alt={documentDetail[estateDocumentField].imgAlt}
        />
      </Grid>
      <Grid className={classes.documentDescription} item xs={12} sm={12}>
        <Typography>{documentDetail[estateDocumentField].description}</Typography>
      </Grid>
      <Grid container justifyContent="center">
        <Grid className={classes.documentAction} item xs={10} sm={10}>
          {currentDocumentId ? (
            <EstateDocumentPaper
              estateDocumentField={estateDocumentField}
              documentId={currentDocumentId}
              estate={estate}
            />
          ) : (
            <DocumentUploadButton
              documentTagId={documentDetail[estateDocumentField].documentTagId}
              folderId={estate.documentsFolderId || ''}
              onUploadComplete={uploadCompleted}
            />
          )}
        </Grid>
        <Grid className={classes.documentAction} item xs={12} sm={12}>
          <Typography variant="caption" className={classes.instruction}>
            {documentDetail[estateDocumentField].instruction}
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
}
